<template>
    <v-container class="mt-8 pb-12" style="max-width: 1100px;">
        <div class="d-flex align-center mb-4">
            <div>
                <h1 class="text-h4 font-weight-bold mb-1">Companies</h1>
                <p class="text-subtitle-1 grey--text mb-0">
                    {{ isSuperAdmin ? 'Manage company details and members' : 'Companies you manage' }}
                </p>
            </div>
            <v-spacer />
            <v-btn v-if="isSuperAdmin" color="primary" class="text-none" @click="createDialog = true">
                <v-icon left>mdi-plus</v-icon>
                Add Company
            </v-btn>
        </div>

        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" placeholder="Search companies"
            outlined dense clearable hide-details class="mb-4" />

        <v-card outlined>
            <v-skeleton-loader v-if="!companyList.length && loading" type="list-item-two-line@4" />
            <v-list v-else-if="filteredCompanies.length" two-line class="py-0">
                <template v-for="(company, i) in filteredCompanies">
                    <v-divider v-if="i > 0" :key="`d-${company.id}`" />
                    <v-list-item :key="company.id" :to="{ name: 'CompanyDetails', params: { id: company.id } }">
                        <v-list-item-avatar color="primary lighten-5">
                            <v-icon color="primary">mdi-domain</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title class="font-weight-medium">{{ company.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ company.description || 'No description' }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action-text class="mr-4">
                            {{ memberCount(company.id) }} member{{ memberCount(company.id) === 1 ? '' : 's' }}
                        </v-list-item-action-text>
                        <v-chip small outlined class="mr-2">{{ company.status || 'ACTIVE' }}</v-chip>
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-list-item>
                </template>
            </v-list>
            <div v-else class="pa-8 text-center grey--text">No companies found</div>
        </v-card>

        <CreateCompanyDialog v-model="createDialog" @company-created="onCompanyCreated" />
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import CreateCompanyDialog from '@/components/projects/dialogs/CreateCompanyDialog.vue';
import { isCompanySuperAdmin, manageableCompanyIds } from './companyAccess';

export default {
    name: 'CompaniesComponent',
    components: { CreateCompanyDialog },
    inject: ['companies', 'users', 'refreshCompanies'],
    data() {
        return { search: '', createDialog: false, loading: true };
    },
    computed: {
        ...mapGetters({ me: 'auth/get_user' }),
        isSuperAdmin() {
            return isCompanySuperAdmin(this.me);
        },
        // Super Admin: every company. Company manager: only the companies they manage.
        companyList() {
            const list = this.companies();
            if (!Array.isArray(list)) return [];
            if (this.isSuperAdmin) return list;
            const allowed = manageableCompanyIds(this.me, this.users());
            return list.filter(c => allowed.has(Number(c.id)));
        },
        filteredCompanies() {
            const q = (this.search || '').trim().toLowerCase();
            const list = [...this.companyList].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            return q ? list.filter(c => (c.name || '').toLowerCase().includes(q)) : list;
        },
    },
    mounted() {
        // Companies are loaded by DefaultLayout; stop showing the skeleton either way
        setTimeout(() => { this.loading = false; }, 1500);
    },
    methods: {
        memberCount(companyId) {
            const list = this.users();
            if (!Array.isArray(list)) return 0;
            return list.filter(u => Array.isArray(u.access) && u.access.some(a => a.company_id === companyId)).length;
        },
        async onCompanyCreated() {
            await this.refreshCompanies();
            this.$toast.success('Company created successfully');
        },
    },
};
</script>
